import * as Yup from 'yup';

import { MediaObject } from 'base/api/types';
import { combineSchemas } from 'common/utils';
import { Fields, PK, SelectOption, StatusOption } from 'common/types';

export type WorkoutFields = {
  title: string;
  description: string;
  status: StatusOption[];
  media_file_pk: MediaObject[];
  content_goals: SelectOption<PK>[];
  is_featured: boolean;
  is_inspiration: boolean;
};

export const fields: Fields<WorkoutFields> = {
  title: {
    label: 'Title',
    name: 'title',
    schema: Yup.string().required().max(55),
  },
  description: {
    label: 'Description',
    name: 'description',
    schema: Yup.string().required().max(500),
  },
  status: {
    label: 'Status',
    name: 'status',
    schema: Yup.array().required(),
  },
  media_file_pk: {
    label: 'Video',
    name: 'media_file_pk',
    schema: Yup.array().required(),
  },
  content_goals: {
    label: 'Categories',
    name: 'content_goals',
    schema: Yup.array(),
  },
  is_featured: {
    label: 'Is this content featured?',
    name: 'is_featured',
  },
  is_inspiration: {
    label: 'Is this content an inspiration?',
    name: 'is_inspiration',
  },
};

export const validationSchema = Yup.object().shape<WorkoutFields>(combineSchemas(fields));

export type WorkoutTagsFields = {
  amount: number;
  amount_unit: SelectOption<string>;
  category: SelectOption<string>[];
  frequency: SelectOption<string>;
  habit: SelectOption<string>[];
  subcategory: SelectOption<string>[];
  term: SelectOption<string>;
  requisites: SelectOption<string>[];
  intensity: SelectOption<string>[];
  muscle_group: SelectOption<string>[];
};

export const tagsFields: Fields<WorkoutTagsFields> = {
  amount: {
    label: 'Amount',
    name: 'amount',
    schema: Yup.number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .test('Is positive?', 'Amount must be greater than 0.', (value) => {
        return typeof value === 'object' || (typeof value === 'number' && value > 0);
      }),
  },
  amount_unit: {
    label: 'Amount unit',
    name: 'amount_unit',
    schema: Yup.array()
      .nullable()
      .when('amount', {
        is: (amount) => {
          return typeof amount === 'number';
        },
        then: Yup.array()
          .nullable()
          .required('Amount and Unit fields must be set together'),
      }),
  },
  category: {
    label: 'Category',
    name: 'category',
    schema: Yup.array().required('Category is a required field'),
  },
  frequency: {
    label: 'Frequency',
    name: 'frequency',
    schema: Yup.number()
      .transform((value) => (isNaN(value) ? null : value))
      .nullable()
      .test('Is positive?', 'Frequency must be greater than 0.', (value) => {
        return typeof value === 'object' || (typeof value === 'number' && value > 0);
      }),
  },
  habit: {
    label: 'Habit',
    name: 'habit',
    schema: Yup.array().required('Habit is a required field'),
  },

  subcategory: {
    label: 'Subcategory',
    name: 'subcategory',
    schema: Yup.array().required('Subcategory is a required field'),
  },
  term: {
    label: 'Term',
    name: 'term',
    schema: Yup.array().nullable(),
  },
  requisites: {
    label: 'Requisites',
    name: 'requisites',
    schema: Yup.array(),
  },
  intensity: {
    label: 'Intensity',
    name: 'intensity',
    schema: Yup.array().required('Intensity is a required field'),
  },
  muscle_group: {
    label: 'Muscle group',
    name: 'muscle_group',
    schema: Yup.array().required('Muscle group is a required field'),
  },
};

export const workoutTagsValidationSchema = Yup.object().shape<WorkoutTagsFields>(
  combineSchemas(tagsFields),
);
